<template>
  <div>
    {{ showNoSprayMarkersForSprayPaths }}
    {{ showSprayReminderMarkersForSprayPaths }}
    {{ fade }}
    {{ mappable.name }}
  </div>
</template>

<script>
import { getCenter } from '../../utils/google-maps';
import Point from '@/store/models/point';
import { mapGetters } from 'vuex';
import * as turf from '@turf/turf';

export default {
  props: {
    google: Object,
    map: Object,
    mappable: Object,
    fade: Boolean,
    clearBound: Function,
    provideBound: Function,
    fitBound: Function,
    mapBounds: Object,
    sprayPathDataPointMarker: Object,
    sprayDataPoint: Object,
    showNoSprayMarkersForSprayPaths: Boolean,
    showSprayReminderMarkersForSprayPaths: Boolean,
    index: Number,
  },

  data() {
    return {
      isActive: false,
      marker: null,
      polygons: [],
      selectedSprayPathDisplays: [],
      noSprayMarkers: [],
    };
  },

  computed: {
    ...mapGetters(['visibleMappables']),
  },

  watch: {
    mapBounds: function(value) {
      const isInsideBounds = this.getIsInsideBounds();
      if(isInsideBounds) {
        this.onActivated();
      } else {
        this.onDeactivated();
      }
    }
  },

  methods: {
    drawPin: function(point) {
      let label;
      const { mappable } = this;
      const { typesLength } = mappable;
      if (typesLength > 1) {
        label = {
          text: typesLength.toString(),
          color:
            mappable.isScheduled && mappable.isMandatory
              ? '#ffffff'
              : '#4469ce',
          fontSize: '24px',
          fontFamily: 'Roboto, sans-serif',
        };
      }

      const onMarkerClick = () => {
        const { $store } = this;
        const { dispatch } = $store;
        dispatch('selectMappable', mappable);
      };

      const [offsetX, offsetY] = mappable.getLabelOffset();
      this.marker = new this.google.maps.Marker({
        position: point,
        map: this.map,
        label: label,
        zIndex: this.index,
        icon: {
          url: mappable.pinSrc,
          labelOrigin: new this.google.maps.Point(offsetX, offsetY),
        },
        opacity: this.fade ? 0.25 : 1,
      });
      this.marker.addListener('click', onMarkerClick);
      this.provideBound(point);

      if (mappable.subscribe) {
        mappable.subscribe(() => {
          const [newOffsetX, newOffsetY] = mappable.getLabelOffset();
          this.marker.setOptions({
            icon: {
              url: mappable.pinSrc,
              labelOrigin: new this.google.maps.Point(newOffsetX, newOffsetY),
            },
          });
        });
      }
    },

    splitSprayPathByPumpStatus: function(points) {
      return points.reduce(
        (acc, point) => {
          const pumpOn = !!point.pumpStatus;
          const targetCollection = acc[pumpOn];
          const otherCollection = acc[!pumpOn];
          const targetLastList = targetCollection[targetCollection.length - 1];
          const otherLastList = otherCollection[otherCollection.length - 1];
          if (otherLastList.length > 0) {
            otherCollection.push([]);
          }
          targetLastList.push(point);
          return acc;
        },
        {
          [true]: [[]],
          [false]: [[]],
        }
      );
    },

    drawSprayPath: function(points) {
      const {
        $store,
        map,
        mappable,
        selectedSprayPathDisplays,
        drawPolyline,
        provideBound,
      } = this;
      const pumpStatusPoints = this.splitSprayPathByPumpStatus(points);
      let polylines = [];
      let fadedPolylines = [];
      pumpStatusPoints[true].forEach((pumpOnPoints) => {
        pumpOnPoints.forEach(provideBound);
        const polyline = drawPolyline(pumpOnPoints, '#22e2c6', 1);
        polyline.setMap(map);
        polylines.push(polyline);

        const fadedPolyline = drawPolyline(pumpOnPoints, '#22e2c6', 0.5);
        fadedPolyline.setMap(map);
        fadedPolylines.push(fadedPolyline);
      });

      pumpStatusPoints[false].forEach((pumpOffPoints) => {
        pumpOffPoints.forEach(provideBound);
        const polyline = drawPolyline(pumpOffPoints, '#ff7777', 1);
        polyline.setMap(map);
        polylines.push(polyline);

        const fadedPolyline = drawPolyline(pumpOffPoints, '#ff7777', 0.5);
        fadedPolyline.setMap(map);
        fadedPolylines.push(fadedPolyline);
      });

      this.fitBound();

      const onPolylineClick = (e) => {
        const pointClicked = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        const getDistance = (a, b) => {
          const latDiff = b.lat - a.lat;
          const lngDiff = b.lng - a.lng;
          const latSqr = latDiff * latDiff;
          const lngSqr = lngDiff * lngDiff;
          return Math.sqrt(latSqr + lngSqr);
        };

        const closestPoint = points.reduce(
          (curr, point, index) => {
            const { distance } = curr;
            const thisDistance = getDistance(point, pointClicked);
            if (thisDistance < distance) {
              return {
                distance: thisDistance,
                index,
                point,
              };
            }
            return curr;
          },
          {
            distance: getDistance(points[0], pointClicked),
            index: 0,
            point: points[0],
          }
        );

        $store.commit('setPathIndex', {
          adulticide: mappable,
          index: closestPoint.index,
        });
      };

      const dataLocationMarker = new google.maps.Marker({
        position: points[0],
        icon: {
          url: require(`@/assets/img/map/focused-path-location-marker.svg`),
          anchor: new google.maps.Point(14, 14),
        },
        opacity: 0,
        shape: {
          coords: [0],
          type: 'circle',
        },
      });
      dataLocationMarker.addListener('click', onPolylineClick);

      mappable.setOnPathDataPointChange((dataPoint) => {
        if (!dataPoint) {
          dataLocationMarker.setOpacity(0);
          return;
        }
        const { lat, lng } = dataPoint;
        const dataPointPosition = { lat, lng };
        dataLocationMarker.setPosition(dataPointPosition);
        dataLocationMarker.setOpacity(1);
      });

      const clickablePolyline = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#ffffff',
        strokeOpacity: 0,
        strokeWeight: 100,
        zIndex: 100,
      });
      clickablePolyline.setMap(this.map);
      clickablePolyline.addListener('click', onPolylineClick);

      return {
        polylines,
        fadedPolylines,
        clickablePolyline,
        dataLocationMarker,
      };
    },

    placeNoSprayMarkers(noSprays) {
      const {
        google,
        map,
        showNoSprayMarkersForSprayPaths,
        showSprayReminderMarkersForSprayPaths,
      } = this;
      const noSprayPin = require('@/assets/img/map/pin-no-spray-active.svg');
      const mapValue = showNoSprayMarkersForSprayPaths ? map : null;
      const noSprayMarkers = noSprays.map((noSpray) => {
        let center = {
          lat: noSpray.latitude,
          lng: noSpray.longitude,
        };
        if (noSpray.usesCustomGeofence) {
          const bounds = new google.maps.LatLngBounds();
          const floatPoints = noSpray.customGeofenceCoordinates.map(
            ({ lat, lon }) => {
              const point = {
                lat: parseFloat(lat),
                lng: parseFloat(lon),
              };
              bounds.extend(point);
              return point;
            }
          );
          this.drawPolygon(floatPoints);
          const boundsCenter = bounds.getCenter();
          center = {
            lat: boundsCenter.lat(),
            lng: boundsCenter.lng(),
          };
        }

        const marker = new google.maps.Marker({
          position: center,
          map: mapValue,
          icon: {
            url: noSprayPin,
          },
        });

        const contentString = `
            <div class="no-spray-info">
                <div class="no-spray-info-title">
                    Do Not Spray
                </div>
                <div>
                    ${noSpray.firstName} ${noSpray.lastName}
                </div>
                <div>
                    ${noSpray.address}
                </div>
                <div>
                    ${noSpray.phone}
                </div>
                <div>
                    Reason: ${!!noSpray.reason ? noSpray.reason : 'n/a'}
                </div>
            </div>
        `;
        const infowindow = new google.maps.InfoWindow({
          content: contentString,
        });
        marker.addListener('click', () => {
          infowindow.open(map, marker);
        });
        return marker;
      });

      return noSprayMarkers;
    },

    placeSprayRequestMarkers(noSprays) {
      // For future use.
    },

    drawPolyline: function(points, color, opacity) {
      return new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: opacity,
        strokeWeight: 4,
      });
    },

    drawPolygon: function(points) {
      const polygon = new this.google.maps.Polygon({
        paths: points,
        strokeColor: '#10b8a8',
        strokeOpacity: 1,
        strokeWeight: 1.6,
        fillColor: '#0dd6c7',
        fillOpacity: 0.4,
      });
      polygon.setMap(this.map);

      const { $store, mappable } = this;
      const onClick = () => {
        const { dispatch } = $store;
        dispatch('selectMappable', mappable);
      };
      polygon.addListener('click', onClick);

      this.polygons = [...this.polygons, polygon];
    },

    handleSprayPath(
      adulticide,
      faded,
      noSpraysVisible,
      sprayRequestsVisible,
      active
    ) {
      const { map, placeNoSprayMarkers } = this;
      const { mapData } = adulticide;
      if (!active) {
        hideAll(mapData.polylines);
        hideAll(mapData.fadedPolylines);
        hideAll(mapData.noSprayMarkers);
        mapData.clickablePolyline.setMap(null);
        mapData.dataLocationMarker.setMap(null);
        return;
      }

      adulticide.onSelectedChanged = (selected) => {
        if (selected === false) {
          hideAll(mapData.polylines);
          showAll(mapData.fadedPolylines);
        } else {
          hideAll(mapData.fadedPolylines);
          showAll(mapData.polylines);
        }
        if (selected) {
          this.clearBound();
          adulticide.requestSpraySessionEntries((entries) => {
            entries.forEach((entry) => {
              this.provideBound(entry);
            });
            this.fitBound();
          });
        }
      };

      mapData.clickablePolyline.setMap(map);
      mapData.dataLocationMarker.setMap(map);

      if (noSpraysVisible) {
        if (!mapData.noSprayMarkers) {
          adulticide.requestNoSprayEntries((entries) => {
            mapData.noSprayMarkers = placeNoSprayMarkers(entries);
          });
        } else {
          showAll(mapData.noSprayMarkers);
        }
      } else {
        hideAll(mapData.noSprayMarkers);
      }

      function hideAll(list) {
        if (!list) {
          return;
        }
        list.forEach((item) => item.setMap(null));
      }

      function showAll(list) {
        list.forEach((item) => item.setMap(map));
      }
    },

    handleGeoJsonPoint(geometry) {
      const [lng, lat] = geometry.coordinates;
      const point = { lat, lng };
      this.drawPin(point);
    },

    handleGeoJsonMultiPoly(geometry) {
      const coordMapper = (coordinates) => {
        const points = coordinates.map(([lng, lat]) => ({ lat, lng }));
        this.drawPolygon(points);
        const firstCoordinate = coordinates[0];
        const lastCoordinate = coordinates[coordinates.length - 1];
        const isLooped = firstCoordinate === lastCoordinate;
        const loopedCoordinate = isLooped
          ? coordinates
          : [...coordinates, coordinates[0]];
        return turf.centerOfMass(turf.polygon([loopedCoordinate]));
      };
      const centerOfMasses = geometry.coordinates.map(coordMapper);
      const collection = turf.featureCollection(centerOfMasses);
      const geojsonCenter = turf.center(collection);
      const [lng, lat] = geojsonCenter.geometry.coordinates;
      const center = { lat, lng };
      this.drawPin(center);
    },

    getIsInsideBounds() {
      const { mappable, mapBounds } = this;
      const { coordinates } = mappable;
      const { lat, lng } = coordinates[0];
      const sw = mapBounds.getSouthWest();
      const ne = mapBounds.getNorthEast();
      const swLat = sw.lat();
      const swLng = sw.lng();
      const neLat = ne.lat();
      const neLng = ne.lng();

      return lat > swLat && lat < neLat && lng > swLng && lng < neLng;
    },

    onActivated() {
      if(this.isActive) {
        return;
      }
      this.isActive = true;
      this.$store.dispatch('alterActiveMarkers', 1);

      const { mappable, marker, polygons, map, fade } = this;
      if (marker) {
        marker.setMap(map);
        marker.setOpacity(fade ? 0.25 : 1);
      }
      if (polygons.length) {
        polygons.forEach((polygon) => polygon.setMap(map));
      }

      if (mappable.mappableType === 'Adulticide') {
        const {
          mappable: adulticide,
          fade,
          showNoSprayMarkersForSprayPaths,
          showSprayReminderMarkersForSprayPaths,
          drawSprayPath,
        } = this;
        if (!adulticide.mapData) {
          adulticide.requestSpraySessionEntries((entries) => {
            adulticide.mapData = {
              ...drawSprayPath(entries),
            };

            this.handleSprayPath(
              adulticide,
              fade,
              showNoSprayMarkersForSprayPaths,
              showSprayReminderMarkersForSprayPaths,
              true
            );
          });
        } else {
          this.handleSprayPath(
            adulticide,
            fade,
            showNoSprayMarkersForSprayPaths,
            showSprayReminderMarkersForSprayPaths,
            true
          );
        }
        return;
      }
    },

    onDeactivated() {
      if(!this.isActive) {
        return;
      }
      this.$store.dispatch('alterActiveMarkers', -1);
      this.isActive = false;

      const {
        mappable,
        fade,
        showNoSprayMarkersForSprayPaths,
        showSprayReminderMarkersForSprayPaths,
      } = this;
      if (mappable.mappableType === 'Adulticide') {
        this.handleSprayPath(
          mappable,
          fade,
          showNoSprayMarkersForSprayPaths,
          showSprayReminderMarkersForSprayPaths,
          false
        );
      }

      if (this.marker) {
        this.marker.setMap(null);
      }
      if (this.polygons.length) {
        this.polygons.forEach((polygon) => polygon.setMap(null));
      }
    }
  },

  mounted() {
    const {
      mappable
    } = this;
    if (mappable.mappableType === 'Adulticide' && mappable.mapData) {
      mappable.requestSpraySessionEntries((entries) => {
        entries.forEach(this.provideBound);
        this.fitBound();
      });
    }
  },

  activated() {
    this.onActivated();
    
    const { mappable } = this;
    if (mappable.isPolygon()) {
      const points = mappable.coordinates.map((coord) => coord.toObject());
      this.drawPolygon(points);
      var allPoints = points;
      if (mappable.additionalCoordinates) {
        mappable.additionalCoordinates.forEach((additionalCoordinatesEntry) => {
          const points = additionalCoordinatesEntry.coordinate_set.map(
            (coord) => {
              return {
                lat: parseFloat(coord.lat),
                lng: parseFloat(coord.lon),
              };
            }
          );
          this.drawPolygon(points);
          allPoints = allPoints.concat(points);
        });
      }
      const center = getCenter(allPoints, this.google);
      this.drawPin(center);
    } else {
      const point = mappable.coordinates[0].toObject();
      this.drawPin(point);
    }
  },

  updated() {
    const {
      mappable,
      fade,
      map,
      selectedSprayPathDisplays,
      showNoSprayMarkersForSprayPaths,
      showSprayReminderMarkersForSprayPaths,
    } = this;
    if (mappable.mappableType === 'Adulticide') {
      this.handleSprayPath(
        mappable,
        fade,
        showNoSprayMarkersForSprayPaths,
        showSprayReminderMarkersForSprayPaths,
        true
      );
    }
    if (this.marker) {
      if (this.fade) {
        this.marker.setOptions({
          opacity: 0.25,
          zIndex: -this.index,
          optimized: false,
        });
      } else {
        this.marker.setOptions({
          opacity: 1,
          zIndex: this.index,
          optimized: true,
        });
      }
    }
    if (this.polygons.length) {
      this.polygons.forEach((polygon) =>
        polygon.setOptions(
          this.fade
            ? {
                strokeColor: '#10b8a8',
                strokeOpacity: 0.5,
                strokeWeight: 1.6,
                fillColor: '#0dd6c7',
                fillOpacity: 0.2,
              }
            : {
                strokeColor: '#10b8a8',
                strokeOpacity: 1,
                strokeWeight: 1.6,
                fillColor: '#0dd6c7',
                fillOpacity: 0.4,
              }
        )
      );
    }
  },

  deactivated() {
    this.onDeactivated();
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

div {
  opacity: 0;
  width: 0;
  height: 0;
}
</style>
